/**
 * Issue #829 — shared policy-upload panel (slot-list model).
 *
 * The panel is a CHECKLIST of the policies the selected frameworks require,
 * not just a list of uploaded files. Each required policy is a slot the user
 * can fill by uploading their own document; un-filled slots are generated by
 * Vera at setup. A bulk drag-and-drop box on top auto-classifies dropped
 * files and fills the matching slots (review-then-import); files that don't
 * confidently match land in an "unrecognized" tray for manual assignment.
 *
 * `mode`:
 *   - 'onboarding' — shows ALL required slots (provided + still-to-generate);
 *     finalize also pre-fills the questionnaire. Persists provided slots to
 *     the wizard via value/onChange.
 *   - 'library'    — shows only the un-uploaded slots (the Policies page
 *     already lists everything); onComplete fires with imported slugs.
 *
 * Backend is unchanged: startImport (parse + classify) → finalizeImports
 * (save content as IMPORTED + draft claims, + onboarding questionnaire).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DragEvent, ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  Upload,
  X,
  Check,
  AlertCircle,
  ChevronDown,
  RotateCw,
  Sparkles,
} from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { FrameworkRefChips } from '@/components/screenata/FrameworkRefChips'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@deepguide-ai/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@deepguide-ai/ui/popover'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'

const ACCEPT = '.pdf,.docx,.md,.html,.htm'
const MAX_BYTES = 10 * 1024 * 1024
const POLL_INTERVAL_MS = 500
/** Fixed body height for the Import dialog library view — keeps the import
 *  action bar pinned while the policy list scrolls. */
const LIBRARY_BODY_CLASS = 'flex h-[440px] min-h-[440px] flex-col'

export type ImportPhase =
  | 'parsing'
  | 'classifying'
  | 'classified'
  | 'saving'
  | 'extracting'
  | 'validating'
  | 'complete'
  | 'failed'

type SlotStatus = 'provided' | 'generated' | 'empty' | 'covered'

interface Slot {
  slug: string
  title: string
  /** Plain-language "what this policy covers / what to provide" guidance. */
  description: string | null
  /** Per-framework citation refs — which requirement(s) this policy satisfies. */
  frameworkRefs: { framework: string; refs: string[] }[]
  status: SlotStatus
  filename: string | null
  /** RFC-191: set when status === 'covered' — the master doc covering this slot. */
  coveredByDocumentId: string | null
  coveredByTitle: string | null
}

/** A slot is "filled" (counts toward coverage, hidden from the open list) when
 *  the user provided their own doc OR it's covered by a consolidated master. */
const isFilledStatus = (status: SlotStatus): boolean =>
  status === 'provided' || status === 'covered'

/** One finished upload — persisted by onboarding so the wizard remembers. */
export interface PolicyUploadEntry {
  policySlug: string | null
  policyTitle: string
  uploadedFilename: string
}

export interface PolicyUploadPanelValue {
  uploads: PolicyUploadEntry[]
}

interface PolicyUploadPanelProps {
  programId?: string
  mode: 'onboarding' | 'library'
  value?: PolicyUploadPanelValue | null
  onChange?: (value: PolicyUploadPanelValue) => void
  onComplete?: (importedSlugs: string[]) => void
  /** Library mode: scope the panel to one policy slot (the empty-policy
   *  page uploads *this* policy, not the whole open library). */
  restrictToSlug?: string
}

interface Pending {
  extractionId: string
  filename: string
  /** Slot this file fills, or null while analyzing / unmatched. */
  assignedSlug: string | null
  /** Per-slot upload: keep the assigned slot, never auto-reassign. */
  forced: boolean
  /** Custom policy (not in the catalog). */
  isCustom: boolean
  customTitle: string
  phase: ImportPhase
  suggestedSlug: string | null
  suggestedTitle: string | null
  /** AI's classification confidence (0–1) for the suggested slug. */
  classifyConfidence: number | null
  /** RFC-191: AI multi-label coverage. >1 entry ⇒ offer the consolidation
   *  prompt (one doc covering many slots). null until classified. */
  coveredSlugs: { slug: string; title: string | null; confidence: number }[] | null
  /** User's confirmed covered-slot set (checkbox state) when in consolidate
   *  mode. null = not consolidating (standard single-slot import). */
  consolidateSlugs: string[] | null
  /** Master doc title for a consolidated import (defaults from filename). */
  consolidatedTitle: string
  errorMessage: string | null
}

export function PolicyUploadPanel({
  programId,
  mode,
  onChange,
  onComplete,
  restrictToSlug,
}: PolicyUploadPanelProps) {
  const queryClient = useQueryClient()
  const [pendingById, setPendingById] = useState<Record<string, Pending>>({})
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const prevCustomCountRef = useRef(0)
  const libraryScrollRef = useRef<HTMLDivElement>(null)
  const bulkInputRef = useRef<HTMLInputElement>(null)
  const customInputRef = useRef<HTMLInputElement>(null)

  const slotsQuery = useQuery({
    ...trpc.policyImport.getUploadSlots.queryOptions({
      programId: programId ?? '',
    }),
    enabled: Boolean(programId),
  })
  const { refetch: refetchSlots } = slotsQuery
  const allSlots = useMemo(
    () => (slotsQuery.data?.slots as Slot[] | undefined) ?? [],
    [slotsQuery.data],
  )
  // Library only shows what's still un-uploaded (the page lists the rest).
  const visibleSlots = useMemo(() => {
    if (mode !== 'library') return allSlots
    const open = allSlots.filter((s) => !isFilledStatus(s.status))
    // Scoped to one policy: keep that slot even if already filled, so the
    // page can offer "replace this document" as well as first upload.
    return restrictToSlug
      ? allSlots.filter((s) => s.slug === restrictToSlug)
      : open
  }, [allSlots, mode, restrictToSlug])
  // RFC-192: slug → its framework requirement refs, for the consolidation
  // checklist to show which requirements each covered policy brings. Built from
  // the FULL catalog (not just open slots) so already-provided covered policies
  // still resolve their refs.
  const refsBySlug = useMemo(
    () => new Map(allSlots.map((s) => [s.slug, s.frameworkRefs])),
    [allSlots],
  )

  const startMutation = useMutation(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    trpc.policyImport.startImport.mutationOptions() as any,
  )
  const finalizeMutation = useMutation(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    trpc.policyImport.finalizeImports.mutationOptions() as any,
  )
  const { mutateAsync: startImport } = startMutation as any
  const { mutateAsync: finalizeImports, isPending: isFinalizing } =
    finalizeMutation as any

  const pending = useMemo(() => Object.values(pendingById), [pendingById])
  const occupiedSlugs = useMemo(
    () =>
      new Set(
        pending
          .filter((p) => p.assignedSlug && !p.isCustom)
          .map((p) => p.assignedSlug as string),
      ),
    [pending],
  )
  const providedSlugs = useMemo(
    () => new Set(allSlots.filter((s) => isFilledStatus(s.status)).map((s) => s.slug)),
    [allSlots],
  )

  // Persist provided slots to the wizard answer (onboarding).
  useEffect(() => {
    if (mode !== 'onboarding' || !onChange) return
    const uploads: PolicyUploadEntry[] = allSlots
      .filter((s) => isFilledStatus(s.status))
      .map((s) => ({
        policySlug: s.slug,
        policyTitle: s.title,
        uploadedFilename: s.filename ?? '',
      }))
    onChange({ uploads })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSlots])

  // ── upload intake ────────────────────────────────────────────────
  const startOne = useCallback(
    async (file: File, opts: { forced?: boolean; isCustom?: boolean; slug?: string }) => {
      if (!programId) return
      const base64 = await fileToBase64(file)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await startImport({
        programId,
        extractQuestionnaire: mode === 'onboarding',
        file: {
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          base64Data: base64,
          size: file.size,
        },
      })
      const extractionId = result.extractionId as string
      setPendingById((prev) => ({
        ...prev,
        [extractionId]: {
          extractionId,
          filename: file.name,
          assignedSlug: opts.slug ?? null,
          forced: Boolean(opts.forced),
          isCustom: Boolean(opts.isCustom),
          customTitle: opts.isCustom ? titleFromFilename(file.name) : '',
          phase: 'parsing',
          suggestedSlug: null,
          suggestedTitle: null,
          classifyConfidence: null,
          coveredSlugs: null,
          consolidateSlugs: null,
          consolidatedTitle: titleFromFilename(file.name),
          errorMessage: null,
        },
      }))
    },
    [programId, mode, startImport],
  )

  const handleBulkFiles = useCallback(
    async (files: File[]) => {
      const accepted = files.filter((f) => f.size <= MAX_BYTES)
      if (accepted.length === 0) return
      setIsUploading(true)
      try {
        // Scoped to one policy: bind the file to that slot instead of letting
        // the classifier choose. The scoped dialog hides the slot list, so a
        // misclassification would be uncorrectable from here.
        for (const file of accepted) {
          await startOne(
            file,
            restrictToSlug ? { forced: true, slug: restrictToSlug } : {},
          )
        }
      } finally {
        setIsUploading(false)
      }
    },
    [startOne, restrictToSlug],
  )

  const handleSlotUpload = useCallback(
    async (slug: string, file: File) => {
      if (file.size > MAX_BYTES) return
      setIsUploading(true)
      try {
        await startOne(file, { forced: true, slug })
      } finally {
        setIsUploading(false)
      }
    },
    [startOne],
  )

  const handleCustomFile = useCallback(
    async (file: File) => {
      if (file.size > MAX_BYTES) return
      setIsUploading(true)
      try {
        await startOne(file, { isCustom: true })
      } finally {
        setIsUploading(false)
      }
    },
    [startOne],
  )

  // ── per-pending poll callback ────────────────────────────────────
  const handleProgress = useCallback(
    (
      extractionId: string,
      data: {
        phase: ImportPhase
        suggestedSlug: string | null
        suggestedTitle: string | null
        classifyConfidence: number | null
        coveredSlugs: { slug: string; title: string | null; confidence: number }[]
        errorMessage: string | null
      },
    ) => {
      setPendingById((prev) => {
        const p = prev[extractionId]
        if (!p) return prev
        // RFC-191: a doc the AI says covers >1 policy is a consolidation
        // candidate — keep it in the tray (don't auto-assign to one slot) so
        // the consolidation checklist can render.
        const isConsolidationCandidate =
          data.coveredSlugs != null && data.coveredSlugs.length > 1
        let assignedSlug = p.assignedSlug
        // Auto-assign a bulk file to its matched slot once classified —
        // unless forced (per-slot upload), the slot is already taken, or it's
        // a consolidation candidate.
        if (
          data.phase === 'classified' &&
          !p.forced &&
          !p.isCustom &&
          !isConsolidationCandidate &&
          assignedSlug === null &&
          data.suggestedSlug
        ) {
          const taken =
            providedSlugs.has(data.suggestedSlug) ||
            Object.values(prev).some(
              (o) =>
                o.extractionId !== extractionId &&
                o.assignedSlug === data.suggestedSlug,
            )
          const isVisible = visibleSlots.some((s) => s.slug === data.suggestedSlug)
          if (!taken && isVisible) assignedSlug = data.suggestedSlug
        }
        // Seed the consolidation checklist once (pre-check the AI-covered slugs
        // that exist in this program's catalog). User can edit before import.
        let consolidateSlugs = p.consolidateSlugs
        if (
          data.phase === 'classified' &&
          p.consolidateSlugs == null &&
          isConsolidationCandidate
        ) {
          const catalogSlugs = new Set(allSlots.map((s) => s.slug))
          // Auto-check only confident matches (≥70%). Low-match policies are
          // left for the user to opt in — checking one claims its requirements
          // covered, so a weak match shouldn't default to checked. The
          // per-row requirements (shown below) make that choice informed.
          consolidateSlugs = data.coveredSlugs
            .filter((c) => c.confidence >= AUTO_CHECK_CONFIDENCE)
            .map((c) => c.slug)
            .filter((slug) => catalogSlugs.has(slug))
        }
        if (
          p.phase === data.phase &&
          p.suggestedSlug === data.suggestedSlug &&
          p.classifyConfidence === data.classifyConfidence &&
          p.assignedSlug === assignedSlug &&
          p.consolidateSlugs === consolidateSlugs &&
          p.errorMessage === data.errorMessage
        ) {
          return prev
        }
        return {
          ...prev,
          [extractionId]: {
            ...p,
            phase: data.phase,
            suggestedSlug: data.suggestedSlug,
            suggestedTitle: data.suggestedTitle,
            classifyConfidence: data.classifyConfidence,
            coveredSlugs: data.coveredSlugs,
            consolidateSlugs,
            errorMessage: data.errorMessage,
            assignedSlug,
          },
        }
      })
    },
    [providedSlugs, visibleSlots, allSlots],
  )

  const updatePending = useCallback(
    (extractionId: string, patch: Partial<Pending>) => {
      setPendingById((prev) =>
        prev[extractionId]
          ? { ...prev, [extractionId]: { ...prev[extractionId], ...patch } }
          : prev,
      )
    },
    [],
  )
  const removePending = useCallback((extractionId: string) => {
    setPendingById((prev) => {
      const next = { ...prev }
      delete next[extractionId]
      return next
    })
  }, [])

  // ── import ───────────────────────────────────────────────────────
  const readyToImport = pending.filter(
    (p) =>
      p.phase === 'classified' &&
      // RFC-191: a consolidation candidate joins the bulk "Import N" action once
      // it has ≥1 covered slot checked (defaults pre-check only confident ≥70%
      // matches). The bulk button finalizes it exactly like its own
      // "Import as one document" card button; candidates with no checked slot
      // still require a manual opt-in, preserving the covered-slot review gate.
      (p.consolidateSlugs != null
        ? p.consolidateSlugs.length > 0 && p.consolidatedTitle.trim().length > 0
        : (p.assignedSlug && !p.isCustom) || (p.isCustom && p.customTitle.trim())),
  )
  const anyAnalyzing = pending.some(
    (p) => p.phase === 'parsing' || p.phase === 'classifying',
  )
  const canImport = !isUploading && !anyAnalyzing && readyToImport.length > 0

  const completionFiredRef = useRef(false)
  const toItem = (p: Pending) => {
    // RFC-191: consolidated import — one master doc covering many slots.
    if (p.consolidateSlugs != null && p.consolidateSlugs.length > 0) {
      return {
        extractionId: p.extractionId,
        slug: null,
        consolidatedTitle: p.consolidatedTitle.trim(),
        coversSlugs: p.consolidateSlugs,
      }
    }
    return {
      extractionId: p.extractionId,
      slug: p.isCustom ? null : (p.assignedSlug as string),
      customTitle: p.isCustom ? p.customTitle.trim() : undefined,
    }
  }
  const runFinalize = useCallback(
    async (items: ReturnType<typeof toItem>[]) => {
      if (items.length === 0) return
      // Optimistically mark importing (immutably).
      setPendingById((prev) => {
        const next = { ...prev }
        for (const i of items) {
          if (next[i.extractionId])
            next[i.extractionId] = { ...next[i.extractionId], phase: 'saving' }
        }
        return next
      })
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await finalizeImports({ programId, items })
      } catch {
        /* per-row state reflects failure via polling */
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [finalizeImports, programId],
  )
  // Batch: also signals library to close the dialog once everything settles.
  const handleImportAll = useCallback(() => {
    completionFiredRef.current = true
    void runFinalize(readyToImport.map(toItem))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyToImport, runFinalize])
  // Per-row: import just this one; never auto-closes the library dialog.
  const handleImportOne = useCallback(
    (p: Pending) => void runFinalize([toItem(p)]),
    [runFinalize],
  )
  // Replace an already-provided policy with this upload. Finalizing against an
  // existing catalog slug overwrites the same PolicyDocument in place (resolved
  // by slug) — no duplicate. We deliberately keep assignedSlug null so the row
  // stays in the tray and shows progress, since the provided slot isn't
  // rendered in library mode.
  const handleReplaceOne = useCallback(
    (p: Pending) => {
      if (!p.suggestedSlug) return
      void runFinalize([
        { extractionId: p.extractionId, slug: p.suggestedSlug, customTitle: undefined },
      ])
    },
    [runFinalize],
  )

  // When an importing pending reaches complete, refetch slots, refresh the
  // surfaces that show policies, and drop the completed pending (its slot is
  // now "provided" from the server).
  const handleTerminal = useCallback(
    (extractionId: string, phase: ImportPhase) => {
      if (phase === 'failed') {
        // Keep the failed row in the tray (its inline error explains why) and
        // surface a toast so the failure isn't missed.
        toast.error('Import failed. Check the file and try again.')
        return
      }
      if (phase !== 'complete') return
      toast.success('Policy imported')
      void refetchSlots()
      void queryClient.invalidateQueries({
        queryKey: trpc.readinessQuestionnaire.getRowState.queryKey(),
      })
      void queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getGenerationItems.queryKey(),
      })
      void queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicies.queryKey(),
      })
      removePending(extractionId)
    },
    [refetchSlots, queryClient, removePending],
  )

  // Library: fire onComplete once everything settles after an import.
  useEffect(() => {
    if (mode !== 'library' || !onComplete) return
    if (pending.length > 0) return
    if (!completionFiredRef.current) return
    completionFiredRef.current = false
    const imported = allSlots
      .filter((s) => s.status === 'provided')
      .map((s) => s.slug)
    onComplete(imported)
  }, [pending.length, mode, onComplete, allSlots])

  // ── drag & drop ──────────────────────────────────────────────────
  const dropDisabled = !programId
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'copy'
    setIsDragging(true)
  }, [])
  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setIsDragging(false)
  }, [])
  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      if (dropDisabled) return
      void handleBulkFiles(Array.from(e.dataTransfer.files ?? []))
    },
    [dropDisabled, handleBulkFiles],
  )

  // ── derived render data ──────────────────────────────────────────
  const pendingBySlot = useMemo(() => {
    const map = new Map<string, Pending>()
    for (const p of pending) {
      if (p.assignedSlug && !p.isCustom) map.set(p.assignedSlug, p)
    }
    return map
  }, [pending])
  // Tray = files not yet bound to a slot: bulk uploads still analyzing, or
  // classified-but-unmatched. Forced (per-slot) uploads carry a slug from
  // the start and render inside their slot, never here.
  const trayPending = pending.filter((p) => !p.isCustom && p.assignedSlug === null)
  const customPending = pending.filter((p) => p.isCustom)

  const providedCount = allSlots.filter((s) => isFilledStatus(s.status)).length
  const totalRequired = allSlots.length
  const remainingCount = Math.max(0, totalRequired - providedCount)

  const providedSlots = visibleSlots.filter((s) => isFilledStatus(s.status))
  const openSlots = visibleSlots.filter((s) => !isFilledStatus(s.status))

  // After "+ Add a policy not listed", scroll so the new custom row is visible.
  useEffect(() => {
    if (mode !== 'library') return
    const customCount = pending.filter((p) => p.isCustom).length
    if (customCount > prevCustomCountRef.current) {
      const scrollToBottom = () => {
        const el = libraryScrollRef.current
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
      }
      requestAnimationFrame(() => requestAnimationFrame(scrollToBottom))
    }
    prevCustomCountRef.current = customCount
  }, [mode, pending])

  const bulkDropSection = (
    <div
      role='button'
      tabIndex={dropDisabled ? -1 : 0}
      aria-disabled={dropDisabled}
      onClick={() => {
        if (!dropDisabled) bulkInputRef.current?.click()
      }}
      onKeyDown={(e) => {
        if (!dropDisabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          bulkInputRef.current?.click()
        }
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'group flex flex-col items-center justify-center gap-1.5 rounded-sm border-2 border-dashed bg-card px-6 py-6 text-center transition-colors',
        dropDisabled
          ? 'cursor-not-allowed border-border'
          : isDragging
            ? 'cursor-pointer border-primary/60 bg-primary/5'
            : 'cursor-pointer border-border-divider hover:border-primary hover:bg-primary/5',
      )}
    >
      {isUploading ? (
        <Loader2 className='h-5 w-5 animate-spin text-status-blue' />
      ) : (
        <Upload className='h-5 w-5 text-text-subtle transition-colors group-hover:text-primary' />
      )}
      <div className='font-body text-[13px] font-medium text-text-secondary'>
        {isDragging
          ? 'Drop to upload'
          : restrictToSlug
            ? 'Drop your document here'
            : 'Drop files and we’ll match them below'}
      </div>
      <div className='font-body text-[12px] text-text-subtle'>
        PDF, DOCX, Markdown, or HTML · up to 10MB{restrictToSlug ? '' : ' each · multiple OK'}
      </div>
      <input
        ref={bulkInputRef}
        type='file'
        accept={ACCEPT}
        multiple
        disabled={dropDisabled}
        className='hidden'
        onChange={(e) => {
          void handleBulkFiles(Array.from(e.target.files ?? []))
          e.target.value = ''
        }}
      />
    </div>
  )

  const incomingTraySection =
    trayPending.length > 0 ? (
      <div className='flex flex-col gap-2'>
        <div className='font-body text-[11px] font-medium uppercase tracking-wide text-text-subtle'>
          Incoming files ({trayPending.length})
        </div>
        {trayPending.map((p) => (
          <TrayRow
            key={p.extractionId}
            pending={p}
            slots={openSlots}
            refsBySlug={refsBySlug}
            occupiedSlugs={occupiedSlugs}
            onAssign={(slug) => updatePending(p.extractionId, { assignedSlug: slug })}
            onReplace={() => handleReplaceOne(p)}
            onRemove={() => removePending(p.extractionId)}
            onToggleCover={(slug) => {
              const cur = p.consolidateSlugs ?? []
              updatePending(p.extractionId, {
                consolidateSlugs: cur.includes(slug)
                  ? cur.filter((s) => s !== slug)
                  : [...cur, slug],
              })
            }}
            onSetConsolidatedTitle={(title) =>
              updatePending(p.extractionId, { consolidatedTitle: title })
            }
            onImportConsolidated={() => handleImportOne(p)}
          />
        ))}
      </div>
    ) : null

  const openSlotRows = openSlots.map((slot) => {
    const p = pendingBySlot.get(slot.slug) ?? null
    return (
      <SlotRow
        key={slot.slug}
        slot={slot}
        pending={p}
        onUpload={(file) => void handleSlotUpload(slot.slug, file)}
        onImport={p ? () => handleImportOne(p) : undefined}
        onClearPending={(id) => removePending(id)}
      />
    )
  })

  const importAllButton =
    readyToImport.length > 0 ? (
      <button
        type='button'
        onClick={handleImportAll}
        disabled={!canImport || isFinalizing}
        className='font-body inline-flex shrink-0 items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
      >
        {isFinalizing ? (
          <>
            <Loader2 className='h-3 w-3 animate-spin' />
            Importing…
          </>
        ) : (
          `Import ${readyToImport.length} ${readyToImport.length === 1 ? 'policy' : 'policies'}`
        )}
      </button>
    ) : null

  const addCustomButton = (
    <button
      type='button'
      onClick={() => customInputRef.current?.click()}
      disabled={dropDisabled}
      className={cn('font-body text-[12px]  mt-3 text-cobalt-strong hover:underline disabled:opacity-50',
        customPending.length > 0?'':'mb-5'
      )}
    >
      + Add a policy not listed
    </button>
  )

  const customFileInput = (
    <input
      ref={customInputRef}
      type='file'
      accept={ACCEPT}
      className='hidden'
      onChange={(e) => {
        const f = e.target.files?.[0]
        if (f) void handleCustomFile(f)
        e.target.value = ''
      }}
    />
  )

  const openSlotsFooter = (
    <>
      <div className='mt-0.5 flex items-center justify-between gap-3'>
        {addCustomButton}
        {importAllButton}
      </div>
      {customFileInput}
    </>
  )

  const openSlotsSection =
    openSlots.length > 0 || mode === 'onboarding' ? (
      <div className='flex flex-col gap-2'>
        {mode === 'onboarding' && (
          <div className='font-body text-[11px] font-medium uppercase tracking-wide text-text-subtle'>
            {providedSlots.length > 0 ? 'Still to provide' : 'Required policies'}
          </div>
        )}
        {openSlotRows}
        {openSlotsFooter}
      </div>
    ) : null

  const providedSlotsSection =
    providedSlots.length > 0 ? (
      <div className='flex flex-col gap-2'>
        <div className='font-body text-[11px] font-medium uppercase tracking-wide text-text-subtle'>
          Provided
        </div>
        {providedSlots.map((slot) => {
          const p = pendingBySlot.get(slot.slug) ?? null
          return (
            <SlotRow
              key={slot.slug}
              slot={slot}
              pending={p}
              onUpload={(file) => void handleSlotUpload(slot.slug, file)}
              onImport={p ? () => handleImportOne(p) : undefined}
              onClearPending={(id) => removePending(id)}
            />
          )
        })}
      </div>
    ) : null

  const customPendingSection =
    customPending.length > 0 ? (
      <div className='flex flex-col gap-4 pt-3 pb-5'>
        <div className='font-body text-[11px] font-medium uppercase tracking-wide text-text-subtle'>
          Other policies
        </div>
        {customPending.map((p) => (
          <CustomRow
            key={p.extractionId}
            pending={p}
            onTitle={(t) => updatePending(p.extractionId, { customTitle: t })}
            onImport={() => handleImportOne(p)}
            onRemove={() => removePending(p.extractionId)}
          />
        ))}
      </div>
    ) : null

  // Library: one scrolling page (drop zone → incoming files → policy list),
  // with the bulk "Import N policies" action pinned to the bottom so it stays
  // reachable no matter how long the list is.
  const libraryView = (
    <div className={LIBRARY_BODY_CLASS}>
      <ScrollArea
        ref={libraryScrollRef}
        className='min-h-0 flex-1'
        viewClassName='[&>div]:!block'
      >
        <div className='flex flex-col gap-4 px-6 pb-1'>
          <div className='flex flex-col gap-2'>
            {!restrictToSlug && (
              <div className='font-body text-[11px] font-medium uppercase tracking-wide text-text-subtle'>
                Upload
              </div>
            )}
            {bulkDropSection}
          </div>
          {incomingTraySection}
          {/* Scoped to one policy: the dialog title already names the target
              and the file is force-assigned to it, so the slot list and
              "add a policy not listed" would only invite the wrong action. */}
          {!restrictToSlug && openSlots.length > 0 && (
            <div className='flex flex-col gap-2'>
              <div className='font-body text-[11px] font-medium uppercase tracking-wide text-text-subtle'>
                Your policies
              </div>
              {openSlotRows}
              <div className='mt-0.5'>{addCustomButton}</div>
            </div>
          )}
          {customPendingSection}
        </div>
      </ScrollArea>
      {importAllButton && (
        <div className='flex shrink-0 items-center justify-end border-t border-border px-6 py-3'>
          {importAllButton}
        </div>
      )}
      {customFileInput}
    </div>
  )

  return (
    <div className={cn('flex w-full flex-col', mode === 'library' ? 'gap-0' : 'gap-4')}>
      {/* Hidden pollers — one per pending, decoupled from where it renders. */}
      {pending.map((p) => (
        <PendingPoller
          key={p.extractionId}
          extractionId={p.extractionId}
          onProgress={handleProgress}
          onTerminal={handleTerminal}
        />
      ))}

      {/* Coverage — sticky so the progress stays visible while scrolling
          the (long) policy list. Opaque bg + border so rows scroll under. */}
      {mode === 'onboarding' && totalRequired > 0 && (
        <div className='font-body bg-background sticky top-0 z-10 flex flex-col gap-1.5 border-b border-border pb-2.5 pt-1 text-[12px]'>
          <div className='flex items-center justify-between gap-3'>
            <span className='font-medium text-text-secondary'>
              {providedCount} of {totalRequired} policies provided
            </span>
            <span className='text-text-subtle'>
              {Math.round((providedCount / totalRequired) * 100)}%
            </span>
          </div>
          <div className='bg-border h-1.5 w-full overflow-hidden rounded-full'>
            <div
              className='h-full rounded-full bg-primary transition-all duration-500'
              style={{
                width: `${Math.round((providedCount / totalRequired) * 100)}%`,
              }}
            />
          </div>
          {remainingCount > 0 && (
            <span className='text-text-subtle'>
              Upload any you already have. Vera generates the remaining{' '}
              {remainingCount}.
            </span>
          )}
        </div>
      )}

      {mode === 'library' ? (
        libraryView
      ) : (
        <>
          {bulkDropSection}
          {incomingTraySection}
          {openSlotsSection}
          {providedSlotsSection}
          {customPendingSection}
        </>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Hidden poller — polls one pending's progress, reports up.
// ─────────────────────────────────────────────────────────────────

function PendingPoller({
  extractionId,
  onProgress,
  onTerminal,
}: {
  extractionId: string
  onProgress: (
    id: string,
    data: {
      phase: ImportPhase
      suggestedSlug: string | null
      suggestedTitle: string | null
      classifyConfidence: number | null
      coveredSlugs: { slug: string; title: string | null; confidence: number }[]
      errorMessage: string | null
    },
  ) => void
  onTerminal: (id: string, phase: ImportPhase) => void
}) {
  const query = useQuery({
    ...trpc.policyImport.getExtractionProgress.queryOptions({ extractionId }),
    refetchInterval: (q) => {
      const p = (q.state.data as { phase?: ImportPhase } | undefined)?.phase
      if (p === 'complete' || p === 'failed') return false
      return POLL_INTERVAL_MS
    },
  })
  const data = query.data as
    | {
        phase: ImportPhase
        suggestedSlug: string | null
        suggestedTitle: string | null
        classifyConfidence: number | null
        coveredSlugs: { slug: string; title: string | null; confidence: number }[]
        errorMessage: string | null
      }
    | undefined

  useEffect(() => {
    if (!data) return
    onProgress(extractionId, {
      phase: data.phase,
      suggestedSlug: data.suggestedSlug,
      suggestedTitle: data.suggestedTitle,
      classifyConfidence: data.classifyConfidence,
      coveredSlugs: data.coveredSlugs ?? [],
      errorMessage: data.errorMessage,
    })
    if (data.phase === 'complete' || data.phase === 'failed') {
      onTerminal(extractionId, data.phase)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.phase])

  return null
}

// ─────────────────────────────────────────────────────────────────
// Slot row — one required policy, optionally overlaid by a pending upload.
// ─────────────────────────────────────────────────────────────────

function SlotRow({
  slot,
  pending,
  onUpload,
  onImport,
  onClearPending,
}: {
  slot: Slot
  pending: Pending | null
  onUpload: (file: File) => void
  onImport?: () => void
  onClearPending: (id: string) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  const isImporting =
    pending?.phase === 'saving' ||
    pending?.phase === 'extracting' ||
    pending?.phase === 'validating'
  const isAnalyzing = pending?.phase === 'parsing' || pending?.phase === 'classifying'
  const isMatched = pending?.phase === 'classified'
  const isFailed = pending?.phase === 'failed'

  let right: ReactNode
  if (pending && (isImporting || isAnalyzing)) {
    right = (
      <span className='font-body inline-flex items-center gap-1.5 text-[12px] text-status-blue-strong'>
        <Loader2 className='h-3.5 w-3.5 animate-spin' />
        {isAnalyzing ? 'Analyzing…' : 'Importing…'}
      </span>
    )
  } else if (pending && isMatched) {
    right = (
      <div className='flex items-center gap-2'>
        {/* Only for AI auto-matches — a per-slot upload was the user's own
            choice, so a "match %" there would be meaningless. */}
        {!pending.forced && <ConfidenceBadge score={pending.classifyConfidence} />}
        <span className='font-body hidden max-w-[160px] truncate text-[12px] text-text-subtle sm:inline'>
          {displayFilename(pending.filename)}
        </span>
        <button
          type='button'
          onClick={onImport}
          className='font-body inline-flex shrink-0 items-center gap-1 rounded-sm bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90'
        >
          Import
        </button>
        <button
          type='button'
          onClick={() => onClearPending(pending.extractionId)}
          aria-label='Remove'
          className='shrink-0 rounded-sm p-1 text-text-subtle hover:bg-background hover:text-text-secondary'
        >
          <X className='h-4 w-4' />
        </button>
      </div>
    )
  } else if (pending && isFailed) {
    right = (
      <span className='font-body inline-flex items-center gap-1.5 text-[12px] text-status-red-strong'>
        <AlertCircle className='h-3.5 w-3.5' />
        Failed
        <button
          type='button'
          onClick={() => onClearPending(pending.extractionId)}
          aria-label='Dismiss'
          className='rounded-sm p-1 hover:bg-background'
        >
          <X className='h-4 w-4' />
        </button>
      </span>
    )
  } else if (slot.status === 'provided') {
    right = (
      <div className='flex items-center gap-2'>
        {slot.filename && (
          <span className='font-body max-w-[200px] truncate text-[12px] text-text-subtle'>
            {displayFilename(slot.filename)}
          </span>
        )}
        <button
          type='button'
          onClick={() => inputRef.current?.click()}
          className='font-body inline-flex shrink-0 items-center gap-1 rounded-sm px-2 py-1 text-[11px] text-text-subtle hover:bg-background hover:text-text-secondary'
        >
          <RotateCw className='h-3 w-3' />
          Replace
        </button>
      </div>
    )
  } else if (slot.status === 'covered') {
    // RFC-191: this policy's content lives in a consolidated master doc. No
    // upload CTA — it's already satisfied by that doc.
    right = (
      <span className='font-body inline-flex shrink-0 items-center gap-1 rounded-md border border-status-green/30 bg-status-green-bg px-2 py-0.5 text-[11px] font-medium text-status-green-strong'>
        <Check className='h-3 w-3' />
        Covered
      </span>
    )
  } else {
    // Accent CTA — visually distinct from the muted grey "Replace" so the
    // "needs your doc" action reads at a glance.
    right = (
      <button
        type='button'
        onClick={() => inputRef.current?.click()}
        className='font-body inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-cobalt-strong/30 bg-cobalt-tonal px-2.5 py-1 text-[11px] font-medium text-cobalt-strong hover:border-cobalt-strong/50 hover:bg-cobalt-tonal-hover'
      >
        <Upload className='h-3 w-3' />
        Upload
      </button>
    )
  }

  const provided =
    slot.status === 'provided' ||
    slot.status === 'covered' ||
    (pending && isMatched)
  const subtitle =
    pending && (isAnalyzing || isImporting || isMatched)
      ? displayFilename(pending.filename) || 'Your upload'
      : slot.status === 'provided'
        ? 'Provided by you'
        : slot.status === 'covered'
          ? `Covered by ${slot.coveredByTitle ?? 'another policy'}`
          : slot.status === 'generated'
            ? 'Vera generated · replace with your own'
            : 'Vera will generate this'
  // Tell the user what the policy is about — exactly when they're deciding
  // what to provide (open slot, no upload in flight). Once a file is matched
  // or the slot is filled (provided/covered), the filename / status carries
  // the row instead.
  const showWhatToProvide =
    !pending && !isFilledStatus(slot.status) && Boolean(slot.description)

  return (
    <div
      className={cn(
        'grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-sm border px-4 py-2.5 transition-colors',
        isAnalyzing || isImporting
          ? 'border-status-blue/30 bg-status-blue-bg'
          : provided
            ? 'border-cobalt-strong/30 bg-card'
            : isFailed
              ? 'border-status-red/30 bg-status-red-bg'
              : 'border-border bg-card',
      )}
    >
      <div className='shrink-0'>
        {provided ? (
          <Check className='h-4 w-4 text-status-green-strong' />
        ) : (
          <Sparkles className='h-4 w-4 text-text-placeholder' />
        )}
      </div>
      <div className='min-w-0'>
        <div className='font-body truncate text-[13px] font-medium text-text-secondary'>
          {slot.title}
        </div>
        <div className='font-body truncate text-[11px] text-text-subtle'>
          {subtitle}
        </div>
        {showWhatToProvide && (
          <div className='font-body mt-0.5 line-clamp-2 text-[11px] leading-snug text-text-placeholder'>
            {slot.description}
          </div>
        )}
        {/* Which framework requirement(s) this policy satisfies. */}
        <FrameworkRefChips refs={slot.frameworkRefs} max={4} />
      </div>
      <div className='shrink-0'>{right}</div>
      <input
        ref={inputRef}
        type='file'
        accept={ACCEPT}
        className='hidden'
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onUpload(f)
          e.target.value = ''
        }}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Tray row — an incoming file that's analyzing or didn't match a slot.
// ─────────────────────────────────────────────────────────────────

function TrayRow({
  pending,
  slots,
  refsBySlug,
  occupiedSlugs,
  onAssign,
  onReplace,
  onRemove,
  onToggleCover,
  onSetConsolidatedTitle,
  onImportConsolidated,
}: {
  pending: Pending
  slots: Slot[]
  refsBySlug: Map<string, { framework: string; refs: string[] }[]>
  occupiedSlugs: Set<string>
  onAssign: (slug: string) => void
  onReplace: () => void
  onRemove: () => void
  onToggleCover: (slug: string) => void
  onSetConsolidatedTitle: (title: string) => void
  onImportConsolidated: () => void
}) {
  const [open, setOpen] = useState(false)
  const analyzing = pending.phase === 'parsing' || pending.phase === 'classifying'
  const importing =
    pending.phase === 'saving' ||
    pending.phase === 'extracting' ||
    pending.phase === 'validating'
  const failed = pending.phase === 'failed'

  // RFC-191: the AI says this doc covers several policies — render the
  // consolidation checklist (import as one master doc) instead of the
  // single-slot picker. Stays in the tray throughout.
  const coveredCandidates = pending.coveredSlugs ?? []
  const isConsolidation = coveredCandidates.length > 1
  if (isConsolidation && !analyzing && !failed) {
    const checked = new Set(pending.consolidateSlugs ?? [])
    const canImport =
      checked.size > 0 && pending.consolidatedTitle.trim().length > 0
    return (
      <div className='flex w-full flex-col gap-2.5 rounded-sm border border-cobalt-strong/30 bg-card px-4 py-3'>
        <div className='flex items-start justify-between gap-3'>
          <div className='min-w-0'>
            <div className='font-body truncate text-[13px] font-medium text-text-secondary'>
              {displayFilename(pending.filename) || pending.filename}
            </div>
            <div className='font-body mt-0.5 text-[11px] leading-snug text-text-subtle'>
              This looks like a consolidated policy. Import it as{' '}
              <span className='font-medium'>one document</span> covering the
              checked policies below.
            </div>
          </div>
          {!importing && (
            <button
              type='button'
              onClick={onRemove}
              aria-label='Remove'
              className='shrink-0 rounded-sm p-1 text-text-subtle hover:bg-background hover:text-text-secondary'
            >
              <X className='h-4 w-4' />
            </button>
          )}
        </div>

        <div className='flex flex-col gap-1'>
          <label
            htmlFor={`consolidated-title-${pending.extractionId}`}
            className='font-body text-[11px] font-medium text-text-subtle'
          >
            Title
          </label>
          <input
            id={`consolidated-title-${pending.extractionId}`}
            type='text'
            value={pending.consolidatedTitle}
            onChange={(e) => onSetConsolidatedTitle(e.target.value)}
            placeholder='Document title, e.g. "HIPAA Compliance Policy"'
            disabled={importing}
            className='font-body h-8 w-full rounded-sm border border-border-divider bg-card px-3 text-[12px] text-text-secondary placeholder:text-text-placeholder focus:border-ring focus:outline-none disabled:opacity-60'
          />
        </div>

        <div className='flex flex-col gap-1'>
          <span
            id={`consolidated-policies-label-${pending.extractionId}`}
            className='font-body text-[11px] font-medium text-text-subtle'
          >
            Policies
          </span>
          <div
            role='group'
            aria-labelledby={`consolidated-policies-label-${pending.extractionId}`}
            className='flex flex-col gap-1'
          >
            {coveredCandidates.map((c) => (
            <label
              key={c.slug}
              className='flex cursor-pointer items-start gap-2 rounded-sm px-1 py-1 hover:bg-surface-subtle'
            >
              <input
                type='checkbox'
                checked={checked.has(c.slug)}
                disabled={importing}
                onChange={() => onToggleCover(c.slug)}
                className='mt-0.5 h-3.5 w-3.5 accent-ring'
              />
              <div className='min-w-0 flex-1'>
                <span className='font-body block truncate text-[12px] text-text-secondary'>
                  {c.title ?? c.slug}
                </span>
                {/* RFC-192: the requirements checking this policy claims as
                    covered — shown so a low-match choice is informed. */}
                <FrameworkRefChips refs={refsBySlug.get(c.slug) ?? []} max={5} />
              </div>
              <ConfidenceBadge score={c.confidence} />
            </label>
            ))}
          </div>
        </div>

        <div className='flex items-center justify-end'>
          <button
            type='button'
            onClick={onImportConsolidated}
            disabled={!canImport || importing}
            className='font-body inline-flex shrink-0 items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
          >
            {importing ? (
              <>
                <Loader2 className='h-3 w-3 animate-spin' />
                Importing…
              </>
            ) : (
              `Import as one document covering ${checked.size} ${checked.size === 1 ? 'policy' : 'policies'}`
            )}
          </button>
        </div>
      </div>
    )
  }
  // A classified file lands here (rather than auto-filling its slot) only when
  // that slot isn't available. Figure out the AI's best guess and *why* it
  // couldn't be applied, so the copy is honest about what to do next.
  const guessSlot = pending.suggestedSlug
    ? (slots.find((s) => s.slug === pending.suggestedSlug) ?? null)
    : null
  const guessClaimed = pending.suggestedSlug
    ? occupiedSlugs.has(pending.suggestedSlug)
    : false
  // Selectable in this picker = a still-open slot, not already claimed here.
  const canAcceptGuess = Boolean(guessSlot) && !guessClaimed
  // The matched policy exists but is already provided — offer to overwrite it.
  const canReplaceGuess = Boolean(pending.suggestedSlug) && !guessSlot

  // Confidence is shown as a pill (below), so keep the note short — the
  // Replace / Choose-another buttons already state the available actions.
  let note = 'No confident match. Choose a policy'
  if (importing) {
    note = `Replacing ${pending.suggestedTitle ?? 'policy'}…`
  } else if (!analyzing && !failed && pending.suggestedTitle) {
    const title = pending.suggestedTitle
    if (canAcceptGuess) note = `Looks like ${title}`
    else if (canReplaceGuess) note = `${title} · already provided`
    else note = `${title} · taken by another upload`
  }
  // Pill goes on the resolved suggestion only, not transient/error states.
  const showConfidence =
    !analyzing && !importing && !failed && Boolean(pending.suggestedTitle)
  const hasPrimaryAction = canAcceptGuess || canReplaceGuess

  return (
    <div
      className={cn(
        'grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-sm border px-4 py-2.5 transition-colors',
        analyzing || importing
          ? 'border-status-blue/30 bg-status-blue-bg'
          : failed
            ? 'border-status-red/30 bg-status-red-bg'
            : 'border-cobalt-strong/30 bg-card',
      )}
    >
      <div className='min-w-0'>
        <div className='font-body truncate text-[13px] font-medium text-text-secondary'>
          {displayFilename(pending.filename) || pending.filename}
        </div>
        <div className='mt-0.5 flex items-start gap-1.5'>
          {showConfidence && <ConfidenceBadge score={pending.classifyConfidence} />}
          <span
            className={cn(
              'font-body line-clamp-2 text-[11px] leading-snug',
              failed ? 'text-status-red-strong' : 'text-text-subtle',
            )}
          >
            {analyzing
              ? 'Reading and classifying the document…'
              : failed
                ? (pending.errorMessage ?? 'Failed')
                : note}
          </span>
        </div>
      </div>
      <div className='flex shrink-0 items-center gap-2'>
        {analyzing || importing ? (
          <span className='font-body inline-flex items-center gap-1.5 text-[12px] font-medium text-status-blue-strong'>
            <Loader2 className='h-4 w-4 animate-spin' />
            {analyzing ? 'Analyzing…' : 'Importing…'}
          </span>
        ) : !failed ? (
          <>
            {/* One-click accept when the guessed slot is actually selectable. */}
            {canAcceptGuess && pending.suggestedSlug && (
              <button
                type='button'
                onClick={() => onAssign(pending.suggestedSlug as string)}
                className='font-body inline-flex shrink-0 items-center gap-1 rounded-sm bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90'
              >
                <Check className='h-3 w-3' />
                Use this
              </button>
            )}
            {/* Overwrite the already-provided policy with this upload. */}
            {canReplaceGuess && (
              <button
                type='button'
                onClick={onReplace}
                className='font-body inline-flex shrink-0 items-center gap-1 rounded-sm bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90'
              >
                <RotateCw className='h-3 w-3' />
                Replace
              </button>
            )}
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <button
                  type='button'
                  className={cn(
                    'font-body inline-flex h-8 min-w-0 items-center justify-between gap-1.5 rounded-sm border border-border-divider bg-card px-3 text-[12px] text-text-secondary hover:border-ring',
                    hasPrimaryAction ? 'w-[150px]' : 'w-[200px]',
                  )}
                >
                  <span className='truncate'>
                    {hasPrimaryAction ? 'Choose another…' : 'Choose a policy…'}
                  </span>
                  <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
                </button>
              </PopoverTrigger>
            <PopoverContent align='end' className='w-[260px] p-0' portalled={false}>
              <Command>
                <CommandInput placeholder='Search policies…' className='h-9 text-[12px]' />
                <CommandList className='max-h-none overflow-visible'>
                  <CommandEmpty>No policies match.</CommandEmpty>
                  <ScrollArea className='w-full' viewClassName='max-h-[260px] [&>div]:!block'>
                    <CommandGroup>
                      {slots.map((s) => {
                        const taken = occupiedSlugs.has(s.slug)
                        return (
                          <CommandItem
                            key={s.slug}
                            value={`${s.title} ${s.slug}`}
                            disabled={taken}
                            className='flex-col items-start gap-0.5'
                            onSelect={() => {
                              onAssign(s.slug)
                              setOpen(false)
                            }}
                          >
                            <span className='font-body text-[13px]'>
                              {s.title}
                              {taken && (
                                <span className='font-body ml-2 text-[11px] text-text-placeholder'>
                                  in use
                                </span>
                              )}
                            </span>
                            {s.description && (
                              <span className='font-body text-[11px] leading-snug text-text-placeholder'>
                                {s.description}
                              </span>
                            )}
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  </ScrollArea>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          </>
        ) : (
          <AlertCircle className='h-4 w-4 text-status-red-strong' />
        )}
        {/* No remove mid-import — the finalize is already running server-side. */}
        {!importing && (
          <button
            type='button'
            onClick={onRemove}
            aria-label='Remove'
            className='shrink-0 rounded-sm p-1 text-text-subtle hover:bg-background hover:text-text-secondary'
          >
            <X className='h-4 w-4' />
          </button>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Custom row — a policy outside the framework catalog.
// ─────────────────────────────────────────────────────────────────

function CustomRow({
  pending,
  onTitle,
  onImport,
  onRemove,
}: {
  pending: Pending
  onTitle: (title: string) => void
  onImport: () => void
  onRemove: () => void
}) {
  const analyzing = pending.phase === 'parsing' || pending.phase === 'classifying'
  const importing =
    pending.phase === 'saving' ||
    pending.phase === 'extracting' ||
    pending.phase === 'validating'
  const busy = analyzing || importing
  const ready = pending.phase === 'classified' && pending.customTitle.trim().length > 0
  return (
    <div className='flex w-full flex-col gap-2 rounded-sm px-0 py-0'>
      <div className='grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3'>
        <input
          type='text'
          value={pending.customTitle}
          onChange={(e) => onTitle(e.target.value)}
          placeholder='Policy title, e.g. "Disaster Recovery Plan"'
          disabled={busy}
          className='font-body h-8 w-full rounded-sm border border-border-divider bg-card px-3 text-[12px] text-text-secondary placeholder:text-text-placeholder focus:border-ring focus:outline-none disabled:opacity-60'
        />
        <div className='flex shrink-0 items-center gap-2'>
          {busy && <Loader2 className='h-4 w-4 animate-spin text-status-blue-strong' />}
          {ready && (
            <button
              type='button'
              onClick={onImport}
              className='font-body inline-flex shrink-0 items-center gap-1 rounded-sm bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90'
            >
              Import
            </button>
          )}
          <button
            type='button'
            onClick={onRemove}
            aria-label='Remove'
            className='rounded-sm p-1 text-text-subtle hover:bg-background hover:text-text-secondary'
          >
            <X className='h-4 w-4' />
          </button>
        </div>
      </div>
      <div className='font-body truncate text-[11px] text-text-subtle'>
        {displayFilename(pending.filename) || pending.filename}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Confidence — how sure the AI is about the suggested policy match.
// Surfaced so the user knows whether to trust the match or override it.
// ─────────────────────────────────────────────────────────────────

// Consolidation rows at/above this match confidence are auto-checked on import;
// weaker matches default unchecked (the user opts in) so we don't over-claim
// coverage on a low-confidence match. Sits with the badge thresholds (0.8/0.5).
const AUTO_CHECK_CONFIDENCE = 0.7

function confidenceMeta(score: number): { label: string; cls: string } {
  if (score >= 0.8)
    return { label: 'High match', cls: 'border-status-green/30 bg-status-green-bg text-status-green-strong' }
  if (score >= 0.5)
    return { label: 'Medium match', cls: 'border-status-orange/30 bg-status-orange-bg text-status-orange-strong' }
  return { label: 'Low match', cls: 'border-status-red/30 bg-status-red-bg text-status-red-strong' }
}

function ConfidenceBadge({ score }: { score: number | null }) {
  if (score == null) return null
  const { label, cls } = confidenceMeta(score)
  const pct = Math.round(score * 100)
  return (
    <span
      title={`${label} · ${pct}% confident`}
      className={cn(
        'font-body inline-flex shrink-0 items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium',
        cls,
      )}
    >
      {pct}% match
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

// Storage keys are "<13+ digit timestamp>-<random>.<ext>" — meaningless to
// the user; never surface them.
const STORAGE_KEY_FILENAME = /^\d{13,}-[a-z0-9]+\.[a-z0-9]+$/i
function displayFilename(name: string | null | undefined): string {
  if (!name) return ''
  return STORAGE_KEY_FILENAME.test(name) ? '' : name
}

function titleFromFilename(filename: string): string {
  const ext = filename.lastIndexOf('.')
  const stem = ext > 0 ? filename.slice(0, ext) : filename
  return stem
    .replace(/[-_.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}
